<template>
    <div class="dlna-queue">
        <div class="dlna-queue__mobile">
            <div class="dlna-queue__mobile-header">
                <div>
                    <div class="dlna-queue__eyebrow">{{ $tt('DLNA播放') }}</div>
                    <h1 class="dlna-queue__mobile-title">{{ $tt('继续聆听') }}</h1>
                </div>
                <div class="dlna-queue__mobile-device">
                    <DLNADeviceSelector @device-selected="onDeviceSelected" />
                </div>
            </div>

            <div v-if="currentTrack" class="dlna-queue__now" @click="goNowPlaying">
                <img
                    :src="currentTrack.image || '/static/dist/img/album_null.jpg'"
                    :alt="currentTrack.title"
                    class="dlna-queue__now-cover"
                >
                <div class="dlna-queue__now-meta">
                    <div class="dlna-queue__now-label">{{ deviceLabel }}</div>
                    <div class="dlna-queue__now-title">{{ currentTrack.title }}</div>
                    <div class="dlna-queue__now-artist">{{ currentTrack.artist || currentTrack.album }}</div>
                </div>
                <button class="dlna-queue__now-button" @click.stop="playPause" :disabled="syncReadonly">
                    <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                    </svg>
                    <svg v-else viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 5h3v14H7zm7 0h3v14h-3z"></path>
                    </svg>
                </button>
            </div>

            <div class="dlna-queue__mobile-actions" v-if="tracks.length > 0">
                <button class="dlna-queue__action" @click="previous" :disabled="syncReadonly">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11 12 21 18V6l-10 6zM3 18l7-6-7-6v12z"></path>
                    </svg>
                    <span>{{ $tt('上一首') }}</span>
                </button>
                <button class="dlna-queue__action is-primary" @click="playPause" :disabled="syncReadonly">
                    <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                    </svg>
                    <svg v-else viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 5h3v14H7zm7 0h3v14h-3z"></path>
                    </svg>
                    <span>{{ isPlaying ? $tt('暂停') : $tt('播放') }}</span>
                </button>
                <button class="dlna-queue__action" @click="next" :disabled="syncReadonly">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 6v12l10-6L3 6zm11 0v12l7-6-7-6z"></path>
                    </svg>
                    <span>{{ $tt('下一首') }}</span>
                </button>
            </div>

            <div class="dlna-queue__tools" v-if="tracks.length > 0">
                <button class="dlna-queue__tool" @click="shuffle" :disabled="syncReadonly">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17 3h4v4h-2V6.41l-3.29 3.3-1.42-1.42L17.59 5H17V3zM3 7h5l3.29 3.29-1.42 1.42L7.17 9H3V7zm18 10v4h-4v-2h.59l-3.3-3.29 1.42-1.42 3.29 3.3V17H21zM3 15h4.17l10-10H21V3h-4v2h.59L7.17 15H3v2z"></path>
                    </svg>
                    <span>{{ $tt('打乱') }}</span>
                </button>
                <button class="dlna-queue__tool" @click="playNow('random')" :disabled="syncReadonly">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2a10 10 0 0 0-7.07 17.07A10 10 0 1 0 19.07 4.93 9.93 9.93 0 0 0 12 2zm0 5a1 1 0 0 1 1 1v3h3a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z"></path>
                    </svg>
                    <span>{{ $tt('随机') }}</span>
                </button>
                <button class="dlna-queue__tool" @click="clear" :disabled="syncReadonly">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9z"></path>
                    </svg>
                    <span>{{ $tt('清空') }}</span>
                </button>
            </div>

            <div v-if="tracks.length > 0" class="dlna-queue__mobile-list">
                <div class="dlna-queue__section-title">
                    <span>{{ $tt('播放队列') }}</span>
                    <span>{{ queueCountText }}</span>
                </div>
                <div
                    v-for="(item, index) in tracks"
                    :key="`${item.id || 'dlna'}-${index}`"
                    :class="['dlna-queue__row', index === queueIndex ? 'is-current' : '']"
                    @click="playTrack(index)"
                >
                    <div class="dlna-queue__row-index">{{ index + 1 }}</div>
                    <img
                        :src="item.image || '/static/dist/img/album_null.jpg'"
                        :alt="item.title"
                        class="dlna-queue__row-cover"
                    >
                    <div class="dlna-queue__row-meta">
                        <div class="dlna-queue__row-title">{{ item.title }}</div>
                        <div class="dlna-queue__row-artist">{{ item.artist || item.album }}</div>
                    </div>
                    <button class="dlna-queue__row-remove" @click.stop="removeAt(index)" :disabled="syncReadonly">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 11h10v2H7z"></path>
                        </svg>
                    </button>
                </div>
            </div>

            <div v-else class="dlna-queue__empty">
                <div class="dlna-queue__empty-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z"></path>
                    </svg>
                </div>
                <div class="dlna-queue__empty-title">{{ $tt('暂无播放') }}</div>
                <div class="dlna-queue__empty-text">{{ $tt('先添加几首歌，再投送到设备') }}</div>
            </div>
        </div>

        <div class="dlna-queue__desktop">
            <aside ref="lyric-section" class="dlna-queue__lyrics lyric-section" v-if="showControl && tracks.length > 0">
                <div v-if="currentTrack" class="dlna-queue__lyrics-current">
                    <router-link :to="{ name: 'playingList', params: { id: currentTrack.id } }" class="dlna-queue__lyrics-cover-link">
                        <img
                            :src="currentTrack.image || '/static/dist/img/album_null.jpg'"
                            :alt="currentTrack.title"
                            class="dlna-queue__lyrics-cover"
                        >
                    </router-link>
                    <div class="dlna-queue__lyrics-meta">
                        <div class="dlna-queue__lyrics-label">{{ $tt('播放：') }}</div>
                        <router-link :to="{ name: 'album', params: { id: currentTrack.albumId } }" class="dlna-queue__lyrics-title">
                            {{ currentTrack.title }}
                        </router-link>
                        <router-link :to="{ name: 'artist', params: { id: currentTrack.artistId } }" class="dlna-queue__lyrics-artist">
                            {{ currentTrack.artist || currentTrack.album }}
                        </router-link>
                    </div>
                    <button class="dlna-queue__icon-button" @click="next" :disabled="syncReadonly">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 6v12l10-6L3 6zm11 0v12l7-6-7-6z"></path>
                        </svg>
                    </button>
                </div>

                <div class="dlna-queue__lyrics-panel">
                    <div class="dlna-queue__lyrics-scroll h-screen-lyric">
                        <ul ref="lyricUL" class="dlna-queue__lyrics-list">
                            <li
                                v-for="(item, i) in lyricsObjArr"
                                :class="['dlna-queue__lyric-line', lyricIndex === i ? 'is-active' : '']"
                                :key="item.uid"
                                :data-index="i"
                                ref="lyric"
                                @click="scrollToLyric(i)"
                            >
                                <div>{{ item.lyric }}</div>
                            </li>
                        </ul>
                        <div v-if="!lyricsObjArr.length" class="dlna-queue__lyrics-empty">{{ $tt('暂无歌词') }}</div>
                    </div>
                </div>
            </aside>

            <div
                class="resize-handle"
                :class="{ resizing: isResizing }"
                @mousedown="startLyricResize"
                v-if="showControl && tracks.length > 0"
            ></div>

            <main class="dlna-queue__panel">
                <header class="dlna-queue__desktop-header">
                    <button class="dlna-queue__collapse" @click="showControl = !showControl">
                        <svg v-if="showControl" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M15.5 5 8.5 12l7 7-1.4 1.4L5.7 12l8.4-8.4L15.5 5zM19 4h2v16h-2V4z"></path>
                        </svg>
                        <svg v-else viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8.5 5l7 7-7 7 1.4 1.4 8.4-8.4-8.4-8.4L8.5 5zM3 4h2v16H3V4z"></path>
                        </svg>
                    </button>
                    <div class="dlna-queue__desktop-title-block">
                        <div class="dlna-queue__eyebrow">{{ $tt('MUSIC') }}</div>
                        <h1 class="dlna-queue__desktop-title">{{ $tt('音乐播放') }}</h1>
                    </div>
                    <div class="dlna-queue__output-switch" role="group" :aria-label="$tt('播放设备选择')">
                        <button
                            :class="['dlna-queue__output-button', currentOutputDevice === 'local' ? 'is-active' : '']"
                            :aria-pressed="currentOutputDevice === 'local'"
                            @click="switchOutputDevice('local')"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z"></path>
                            </svg>
                            <span>{{ $tt('音乐播放') }}</span>
                        </button>
                        <button
                            :class="['dlna-queue__output-button', currentOutputDevice === 'xiaomi' ? 'is-active' : '']"
                            :aria-pressed="currentOutputDevice === 'xiaomi'"
                            @click="switchOutputDevice('xiaomi')"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M7 4h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm1 2v12h8V6H8zm4 3a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"></path>
                            </svg>
                            <span>{{ $tt('小爱音箱') }}</span>
                        </button>
                    </div>
                    <button class="dlna-queue__groupcast-button" @click="goGroupCast">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 7h5v2H7zm0 4h10v2H7zm0 4h7v2H7z"></path>
                            <path d="M17 8V5l4 4-4 4v-3h-3V8zM17 19v-3h-3v-2h3v-3l4 4-4 4z"></path>
                        </svg>
                        <span>{{ $tt('多端同播') }}</span>
                    </button>
                    <div class="dlna-queue__status" v-if="isDlnaMode && currentDlnaDevice">
                        {{ deviceLabel }}
                    </div>
                    <div class="dlna-queue__selector">
                        <DLNADeviceSelector @device-selected="onDeviceSelected" />
                    </div>
                </header>

                <section v-if="currentTrack" class="dlna-queue__desktop-now">
                    <img
                        :src="currentTrack.image || '/static/dist/img/album_null.jpg'"
                        :alt="currentTrack.title"
                        class="dlna-queue__desktop-cover"
                    >
                    <div class="dlna-queue__desktop-meta">
                        <div class="dlna-queue__desktop-label">{{ $tt('当前播放') }}</div>
                        <div class="dlna-queue__desktop-track">{{ currentTrack.title }}</div>
                        <div class="dlna-queue__desktop-artist">{{ currentTrack.artist || currentTrack.album }}</div>
                    </div>
                    <div class="dlna-queue__desktop-controls">
                        <button class="dlna-queue__control" @click="previous" :disabled="syncReadonly">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M11 12 21 18V6l-10 6zM3 18l7-6-7-6v12z"></path>
                            </svg>
                        </button>
                        <button class="dlna-queue__control is-play" @click="playPause" :disabled="syncReadonly">
                            <svg v-if="!isPlaying" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                            </svg>
                            <svg v-else viewBox="0 0 24 24" fill="currentColor">
                                <path d="M7 5h3v14H7zm7 0h3v14h-3z"></path>
                            </svg>
                        </button>
                        <button class="dlna-queue__control" @click="next" :disabled="syncReadonly">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 6v12l10-6L3 6zm11 0v12l7-6-7-6z"></path>
                            </svg>
                        </button>
                    </div>
                </section>

                <section class="dlna-queue__desktop-toolbar" v-if="tracks.length > 0">
                    <button class="dlna-queue__desktop-tool" @click="shuffle" :disabled="syncReadonly">
                        {{ $tt('打乱顺序') }}
                    </button>
                    <button class="dlna-queue__desktop-tool" @click="playNow('random')" :disabled="syncReadonly">
                        {{ $tt('随机播放') }}
                    </button>
                    <button class="dlna-queue__desktop-tool" @click="clear" :disabled="syncReadonly">
                        {{ $tt('清空队列') }}
                    </button>
                </section>

                <section class="dlna-queue__tracklist" v-if="tracks.length > 0">
                    <div class="dlna-queue__section-title">
                        <span>{{ $tt('播放队列') }}</span>
                        <span>{{ queueCountText }}</span>
                    </div>
                    <TrackList :tracks="tracks" no-artist :index-field="'index'" :show-image="true" :readonly="syncReadonly" />
                </section>

                <div v-else class="dlna-queue__empty dlna-queue__empty--desktop">
                    <div class="dlna-queue__empty-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z"></path>
                        </svg>
                    </div>
                    <div class="dlna-queue__empty-title">{{ $tt('暂无播放') }}</div>
                    <div class="dlna-queue__empty-text">{{ $tt('先添加几首歌，再投送到设备') }}</div>
                </div>
            </main>
        </div>
    </div>
</template>
<script>
import TrackAction from "@/views/library/track/TrackAction";
import DLNADeviceSelector from '@/components/dlna/DLNADeviceSelector'
import { mapGetters } from 'vuex'
import TrackList from '@/views/library/track/TrackList'

export default {
    components: {
        TrackAction,
        DLNADeviceSelector,
        TrackList
    },
    data() {
        return {
            showControl: true,
            lyricIndex: 0,
            lyricsObjArr: [],
            timer: null,
            refreshTimer: null,
            syncRoomInput: '',
            syncBusy: false,
            // 拖拽相关状态
            isResizing: false,
            startX: 0,
            startWidth: 0,
            // 定时器唯一标识，用于调试
            timerInstanceId: Math.random().toString(36).substr(2, 9),
        }
    },
    computed: {
        ...mapGetters(['trackId']),

        tracks() {
            return this.$store.state.common.queue || []
        },
        isPlaying() {
            return this.$store.state.common.isPlaying
        },
        queueIndex() {
            return this.$store.state.common.queueIndex
        },
        songImageWall() {
            const currentTrack = this.tracks[this.queueIndex]
            if (currentTrack && currentTrack.image) {
                return currentTrack.image.replace('al-', 'alw-')
            }
            return null
        },
        backgroundImage() {
            const currentTrack = this.tracks[this.queueIndex]
            return `url('${currentTrack?.image || '/static/dist/img/album_null.jpg'}')`
        },
        remainingTime() {
            const currentTrack = this.tracks[this.queueIndex]
            if (!currentTrack) return 0
            return currentTrack.duration - this.currentTime
        },
        isDlnaMode() {
            return this.$store.getters.isDlnaMode
        },
        isDlnaConnected() {
            return this.$store.getters.isDlnaConnected
        },
        currentDlnaDevice() {
            return this.$store.getters.currentDlnaDevice
        },
        availableDlnaDevices() {
            return this.$store.getters.availableDlnaDevices
        },
        currentTrack() {
            return this.tracks[this.queueIndex] || null
        },
        deviceLabel() {
            if (!this.isDlnaMode || !this.currentDlnaDevice) {
                return this.$tt('本地播放')
            }
            if (this.currentDlnaDevice.udn === 'xiaoai_speaker_fixed') {
                return this.$tt('小爱音箱')
            }
            return this.currentDlnaDevice.device_info?.name || 'DLNA'
        },
        currentOutputDevice() {
            return this.$route.name === 'playing-xiaomi' ? 'xiaomi' : 'local'
        },
        queueCountText() {
            return `${this.tracks.length} ${this.$tt('首')}`
        },
        syncRoomId() {
            return this.$store.state.syncRoom.roomId
        },
        syncOnlineCount() {
            return this.$store.state.syncRoom.onlineCount
        },
        syncIsOwner() {
            return this.$store.getters['syncRoom/isOwner']
        },
        syncReadonly() {
            return !!this.syncRoomId && !this.syncIsOwner
        },
        currentTime: {
            get() {
                return this.$store.state.common.currentTime
            },
            set(value) {
                this.$store.state.common.currentTime = value
            }
        },
    },
    created() {
        // 初始化播放器状态
        // this.$store.dispatch('initializePlayer')
        if (this.isDlnaMode) {
            this.startTimer()
        }
        
        if (this.tracks.length !== 0 && this.queueIndex !== -1) {
            this.$api.subsonic.getLyrics(this.tracks[this.queueIndex].id).then((res) => {
                this.lyricsObjArr = this.formatLyrics(res.value)
            })
        }
    },
    mounted() {
        // 添加拖拽事件监听器
        document.addEventListener('mousemove', this.handleLyricResize)
        document.addEventListener('mouseup', this.stopLyricResize)
        
        // 添加窗口大小变化监听器
        window.addEventListener('resize', this.handleWindowResize)
        
        // 初始化歌词区域宽度
        this.$nextTick(() => {
            if (this.$refs['lyric-section']) {
                this.$refs['lyric-section'].style.width = '384px' // 对应原来的 w-96
            }
        })
    },
    beforeDestroy() {
        this.stopTimer()
        // 移除事件监听器
        document.removeEventListener('mousemove', this.handleLyricResize)
        document.removeEventListener('mouseup', this.stopLyricResize)
        window.removeEventListener('resize', this.handleWindowResize)
    },
    watch: {
        '$store.state.common.currentTime': {
            handler(newTime) {
                // 移除冗余赋值，因为 currentTime 的 getter 已经返回了 store 中的值
                // 直接使用 newTime 参数来避免重复更新
                for (let i = 0; i < this.lyricsObjArr.length; i++) {
                    if (newTime > (parseInt(this.lyricsObjArr[i].time))) {
                        const index = this.$refs.lyric?.[i]?.dataset?.index
                        if (i === parseInt(index)) {
                            this.lyricIndex = i
                            if (this.$refs.lyricUL) {
                                this.$refs.lyricUL.style.transform = `translateY(${170 - (30 * (i + 1))}px)`
                            }
                        }
                    }
                }
            }
        },
        // 监听播放状态变化，管理定时器
        isPlaying(newVal) {
            console.log(`[${this.timerInstanceId}] 播放状态变化: ${newVal}`)
            if (this.isDlnaMode) {
                if (newVal) {
                    // 开始播放时启动定时器
                    if (!this.timer) {
                        console.log(`[${this.timerInstanceId}] 播放开始，启动定时器`)
                        this.startTimer()
                    }
                } else {
                    // 暂停时停止定时器
                    console.log(`[${this.timerInstanceId}] 播放暂停，停止定时器`)
                    this.stopTimer()
                }
            }
        },
        // 监听 DLNA 模式变化
        isDlnaMode(newVal) {
            console.log(`[${this.timerInstanceId}] DLNA模式变化: ${newVal}`)
            if (newVal && this.isPlaying) {
                // 切换到 DLNA 模式且正在播放时，启动定时器
                if (!this.timer) {
                    console.log(`[${this.timerInstanceId}] 切换到DLNA模式，启动定时器`)
                    this.startTimer()
                }
            } else {
                // 切换到本地模式时，停止定时器
                console.log(`[${this.timerInstanceId}] 切换到本地模式，停止定时器`)
                this.stopTimer()
            }
        },
        trackId() {
            if (this.trackId === null || this.trackId === -1) {
                return
            }
            if (this.$route.params.id !== this.trackId) {
                this.$api.subsonic.getSong(this.trackId).then((res) => {
                    this.song = res
                })
                this.$api.subsonic.getLyrics(this.trackId).then((res) => {
                    this.lyricsObjArr = this.formatLyrics(res.value)
                })
            }
        }
    },
    methods: {
        switchOutputDevice(deviceType) {
            localStorage.setItem('currentDevice', deviceType)
            const targetName = deviceType === 'xiaomi' ? 'playing-xiaomi' : 'playing-local'
            this.pushRoute({ name: targetName })
        },
        goGroupCast() {
            this.pushRoute({ name: 'groupcast' })
        },
        pushRoute(route) {
            if (route.name === this.$route.name) return
            this.$router.push(route).catch(() => {})
        },
        startTimer() {
            if (this.timer) {
                console.log(`[${this.timerInstanceId}] 清除旧定时器`)
                clearInterval(this.timer)
            }
            console.log(`[${this.timerInstanceId}] 启动新定时器`)
            this.timer = setInterval(() => {
                if (this.isDlnaMode && this.isPlaying) {
                    // 确保 currentTime 是数字类型，避免字符串拼接
                    this.currentTime = Number(this.currentTime) + 1
                    console.log(`[${this.timerInstanceId}] currentTime`, this.currentTime)
                    console.log('duration', this.$store.state.common.duration)
                    // console.log('当前歌曲信息:', this.tracks[this.queueIndex])
                    if (this.currentTime >= this.$store.state.common.duration) {
                        console.warn('⚠️ 达到歌曲时长，准备切换下一首')
                        this.$store.dispatch('next')
                    }
                }
            }, 1000)
        },
        stopTimer() {
            if (this.timer) {
                console.log(`[${this.timerInstanceId}] 停止定时器`)
                clearInterval(this.timer)
                this.timer = null
            }
        },
        async playTrack(index) {
            if (this.syncReadonly) {
                return
            }
            if (index === this.queueIndex && this.isPlaying) {
                await this.$store.dispatch('playPause')
            } else {
                await this.$store.dispatch('playTrackList', {
                    tracks: this.tracks,
                    index
                })
            }
        },
        removeAt(index) {
            if (this.syncReadonly) {
                return
            }
            this.$store.commit('removeFromQueue', index)
        },
        goNowPlaying() {
            if (!this.currentTrack) {
                return
            }
            this.$router.push({ name: 'playingList', params: { id: this.currentTrack.id } })
        },
        async playPause() {
            if (this.syncReadonly) {
                return
            }
            await this.$store.dispatch('playPause')
        },
        async next() {
            if (this.syncReadonly) {
                return
            }
            await this.$store.dispatch('next')
        },
        async previous() {
            if (this.syncReadonly) {
                return
            }
            await this.$store.dispatch('previous')
        },
        clear() {
            if (this.syncReadonly) {
                return
            }
            this.$store.commit('clearQueue')
        },
        shuffle() {
            if (this.syncReadonly) {
                return
            }
            this.$store.commit('shuffleQueue')
        },
        async playNow(id) {
            if (this.syncReadonly) {
                return
            }
            const playlist = await this.$api.subsonic.getPlaylist(id)
            return this.$store.dispatch('playTrackList', {
                tracks: playlist.tracks
            })
        },
        async onDeviceSelected(device) {
            try {
                if (device) {
                    await this.$store.dispatch('connectToDlnaDevice', device)
                    this.$myMsg.notify({
                        title: this.$tt('已切换到DLNA播放'),
                        content: `已连接到 ${device.device_info.name}`,
                        type: 'success',
                        time: 1000,
                    })
                } else {
                    await this.$store.dispatch('switchToLocalPlay')
                    this.$myMsg.notify({
                        title: this.$tt('已切换到本地播放'),
                        type: 'info',
                        time: 1000,
                    })
                }
            } catch (error) {
                console.error('切换DLNA设备失败:', error)
                this.$myMsg.notify({
                    title: this.$tt('设备切换失败'),
                    content: error.message || this.$tt('未知错误'),
                    type: 'error',
                    time: 3000,
                })
            }
        },
        formatDuration(value) {
            if (!isFinite(value)) {
                return '∞'
            }
            const minutes = Math.floor(value / 60)
            const seconds = Math.floor(value % 60)
            return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds
        },
        formatSize(value) {
            return (value / 1024 / 1024).toFixed(1) + ' MB'
        },
        formatLyrics(lyrics) {
            if (!lyrics) return []
            const regNewLine = /\n/
            const lineArr = lyrics.split(regNewLine) // 每行歌词的数组

            const lyricsObjArr = []
            lineArr.forEach(item => {
                if (item === '') return
                const spiltList = item.split(']')
                const spiltListLength = spiltList.length
                const content = spiltList[spiltListLength - 1].trim()

                const timeArray = spiltList.slice(0, spiltListLength - 1)
                timeArray.forEach(dt => {
                    const obj = {}
                    obj.lyric = content === '' ? '' : content
                    obj.time = this.formatLyricTime(dt.slice(1))
                    obj.uid = Math.random().toString().slice(-6)
                    if (obj.lyric === '') {
                        console.log('')
                    } else {
                        lyricsObjArr.push(obj)
                    }
                })
            })
            return lyricsObjArr.sort((a, b) => a.time - b.time)
        },
        formatLyricTime(item) { // 格式化歌词的时间 转换成 sss:ms
            if (item.indexOf('.') === -1) {
                const timeParts = item.split(':')
                const hours = parseInt(timeParts[0])
                const minutes = parseInt(timeParts[1])
                const seconds = parseInt(timeParts[2])
                const totalTime = hours * 60 * 60 + minutes * 60 + seconds
                return Number(totalTime)
            } else {
                const regTime = /\d{2}:\d{2}.\d{2,3}/
                const isTime = item.match(regTime)
                const time = isTime ? isTime[0] : '00:00.00'
                const regMin = /.*:/
                const regSec = /:.*\./
                const regMs = /\./

                const min = parseInt(time.match(regMin)[0].slice(0, 2))
                let sec = parseInt(time.match(regSec)[0].slice(1, 3))
                const ms = time.slice(time.match(regMs).index + 1, time.match(regMs).index + 3)
                if (min !== 0) {
                    sec += min * 60
                }
                return Number(sec + '.' + ms)
            }
        },
        scrollToLyric(i) {
            if (this.syncReadonly) {
                return
            }
            const currentTime = this.lyricsObjArr[i].time
            const seek = currentTime / this.$store.state.common.duration
            this.$store.dispatch('seek', seek)
        },
        dragStart(event, index) {
            this.draggedIndex = index;
            event.dataTransfer.effectAllowed = 'move';
            event.target.style.opacity = '0.4';
        },
        dragEnd(event) {
            const rows = event.target.parentElement.getElementsByTagName('tr');
            for (let row of rows) {
                row.style.opacity = '1';
            }
        },
        drop(event, index) {
            event.preventDefault();
            console.log('拖拽重排序功能待实现')
        },
        
        // 初始化DLNA功能
        async initializeDlna() {
            try {
                console.log('初始化DLNA功能...')
                // 发现DLNA设备
                await this.$store.dispatch('discoverDlnaDevices')
            } catch (error) {
                console.warn('初始化DLNA功能失败:', error)
            }
        },

        // 测试DLNA功能
        testDlnaFeatures() {
            console.log('=== DLNA功能测试 ===')
            console.log('当前播放模式:', this.$store.state.common.playMode)
            console.log('是否DLNA模式:', this.isDlnaMode)
            console.log('是否DLNA连接:', this.isDlnaConnected)
            console.log('当前DLNA设备:', this.currentDlnaDevice)
            console.log('可用DLNA设备:', this.availableDlnaDevices)
            console.log('AudioController实例:', window.audioController)
            console.log('================')
        },

        // 歌词区域拖拽相关方法
        startLyricResize(e) {
            this.isResizing = true
            this.startX = e.clientX
            this.startWidth = this.$refs['lyric-section'].offsetWidth
        },
        handleLyricResize(e) {
            if (!this.isResizing) return
            
            const width = this.startWidth + (e.clientX - this.startX)
            const minLyricWidth = 300 // 歌词区域最小宽度
            const minPlayingWidth = 400 // 正在播放区域最小宽度
            const resizeHandleWidth = 4 // 拖拽手柄宽度
            const containerPadding = 24 // 容器边距
            
            // 计算最大允许宽度：总宽度 - 正在播放最小宽度 - 拖拽手柄宽度 - 边距
            const maxLyricWidth = window.innerWidth - minPlayingWidth - resizeHandleWidth - containerPadding
            
            // 确保宽度在合理范围内
            const finalWidth = Math.max(minLyricWidth, Math.min(width, maxLyricWidth))
            
            if (this.$refs['lyric-section']) {
                this.$refs['lyric-section'].style.width = `${finalWidth}px`
            }
        },
        stopLyricResize() {
            this.isResizing = false
        },
        handleWindowResize() {
            // 窗口大小变化时检查歌词区域宽度是否超出限制
            if (this.$refs['lyric-section']) {
                const currentWidth = this.$refs['lyric-section'].offsetWidth
                const minLyricWidth = 300
                const minPlayingWidth = 400
                const resizeHandleWidth = 4
                const containerPadding = 24
                
                const maxLyricWidth = window.innerWidth - minPlayingWidth - resizeHandleWidth - containerPadding
                
                if (currentWidth > maxLyricWidth) {
                    this.$refs['lyric-section'].style.width = `${maxLyricWidth}px`
                }
            }
        },
    }
}
</script>
<style scoped>
.dlna-queue {
    width: 100%;
}

.dlna-queue__mobile {
    height: calc(100dvh - 12.25rem);
    padding: 0.75rem 0.35rem 0.25rem;
    box-sizing: border-box;
    overflow-y: auto;
}

.dlna-queue__desktop {
    display: none;
}

.dlna-queue__mobile-header,
.dlna-queue__desktop-header,
.dlna-queue__section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.dlna-queue__mobile-header {
    margin-bottom: 1rem;
}

.dlna-queue__eyebrow {
    font-size: 0.8rem;
    font-weight: 700;
    color: #94a3b8;
}

.dlna-queue__mobile-title {
    margin-top: 0.18rem;
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.dlna-queue__mobile-device {
    display: flex;
    justify-content: flex-end;
    min-width: 2.8rem;
}

.dlna-queue__now {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding: 0.95rem;
    border-radius: 1.7rem;
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.96), rgba(31, 41, 55, 0.92));
    color: #fff;
    box-shadow: 0 20px 46px rgba(15, 23, 42, 0.18);
}

.dlna-queue__now-cover {
    width: 4rem;
    height: 4rem;
    object-fit: cover;
    border-radius: 1rem;
    flex-shrink: 0;
}

.dlna-queue__now-meta,
.dlna-queue__row-meta,
.dlna-queue__desktop-meta,
.dlna-queue__lyrics-meta {
    min-width: 0;
    flex: 1;
}

.dlna-queue__now-label {
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.65);
}

.dlna-queue__now-title,
.dlna-queue__now-artist,
.dlna-queue__row-title,
.dlna-queue__row-artist,
.dlna-queue__desktop-track,
.dlna-queue__desktop-artist,
.dlna-queue__lyrics-title,
.dlna-queue__lyrics-artist {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.dlna-queue__now-title {
    margin-top: 0.25rem;
    font-size: 1rem;
    font-weight: 700;
}

.dlna-queue__now-artist {
    margin-top: 0.2rem;
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.68);
}

.dlna-queue__now-button,
.dlna-queue__icon-button,
.dlna-queue__control,
.dlna-queue__collapse,
.dlna-queue__row-remove {
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.dlna-queue__now-button {
    width: 2.85rem;
    height: 2.85rem;
    background: #fff;
    color: #111827;
}

.dlna-queue__now-button svg {
    width: 1.25rem;
    height: 1.25rem;
}

.dlna-queue__mobile-actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.7rem;
    margin-top: 1rem;
}

.dlna-queue__action,
.dlna-queue__tool {
    border: 0;
    border-radius: 1.2rem;
    background: rgba(255, 255, 255, 0.8);
    color: #111827;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.42rem;
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.1);
    font-size: 0.75rem;
    font-weight: 700;
}

.dlna-queue__action {
    min-height: 3.45rem;
    flex-direction: column;
}

.dlna-queue__action.is-primary {
    background: #111827;
    color: #fff;
}

.dlna-queue__action svg,
.dlna-queue__tool svg {
    width: 1.15rem;
    height: 1.15rem;
}

.dlna-queue__tools {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.7rem;
    margin-top: 0.7rem;
}

.dlna-queue__tool {
    min-height: 2.7rem;
}

.dlna-queue__mobile-list {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.dlna-queue__section-title {
    margin: 0.15rem 0 0.1rem;
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 800;
}

.dlna-queue__section-title span:last-child {
    color: #94a3b8;
    font-weight: 700;
}

.dlna-queue__row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.8rem 0.7rem;
    border-radius: 1.35rem;
    background: rgba(255, 255, 255, 0.74);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
    transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.dlna-queue__row:active,
.dlna-queue__action:active,
.dlna-queue__tool:active,
.dlna-queue__now-button:active {
    transform: translateY(1px);
}

.dlna-queue__row.is-current {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(226, 232, 240, 0.86));
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.14);
}

.dlna-queue__row-index {
    width: 1.2rem;
    color: #94a3b8;
    font-size: 0.78rem;
    font-weight: 700;
    text-align: center;
    flex-shrink: 0;
}

.dlna-queue__row-cover {
    width: 3.15rem;
    height: 3.15rem;
    object-fit: cover;
    border-radius: 0.95rem;
    flex-shrink: 0;
}

.dlna-queue__row-title {
    color: #111827;
    font-size: 0.94rem;
    font-weight: 700;
}

.dlna-queue__row-artist {
    margin-top: 0.2rem;
    color: #94a3b8;
    font-size: 0.76rem;
}

.dlna-queue__row-remove {
    width: 2rem;
    height: 2rem;
    background: transparent;
    color: #94a3b8;
}

.dlna-queue__row-remove svg {
    width: 1rem;
    height: 1rem;
}

.dlna-queue__empty {
    margin-top: 1rem;
    padding: 3.5rem 1.25rem;
    border-radius: 1.55rem;
    background: rgba(255, 255, 255, 0.72);
    color: #94a3b8;
    text-align: center;
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
}

.dlna-queue__empty-icon {
    width: 3rem;
    height: 3rem;
    margin: 0 auto 0.85rem;
    border-radius: 999px;
    background: rgba(15, 23, 42, 0.06);
    color: #111827;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.dlna-queue__empty-icon svg {
    width: 1.35rem;
    height: 1.35rem;
}

.dlna-queue__empty-title {
    color: #111827;
    font-size: 1rem;
    font-weight: 800;
}

.dlna-queue__empty-text {
    margin-top: 0.28rem;
    font-size: 0.82rem;
    font-weight: 700;
}

.h-screen-lyric {
    height: calc(100dvh - 245px);
}

.lyric-section {
    width: 384px;
    min-width: 300px;
    flex-shrink: 0;
}

.resize-handle {
    width: 4px;
    background-color: transparent;
    cursor: col-resize;
    transition: background-color 0.2s;
    position: relative;
    flex-shrink: 0;
}

.resize-handle::after {
    content: '';
    position: absolute;
    top: 0;
    left: -3px;
    width: 10px;
    height: 100%;
    background-color: transparent;
}

.resize-handle.resizing,
.resize-handle:hover {
    background-color: rgba(15, 23, 42, 0.12);
}

@media (min-width: 768px) {
    .dlna-queue {
        height: calc(100dvh - 5rem);
        padding: 0.65rem;
        border-radius: 1.25rem;
        background:
            radial-gradient(circle at 8% 0%, rgba(238, 243, 255, 0.9), transparent 28rem),
            radial-gradient(circle at 88% 12%, rgba(247, 246, 243, 0.95), transparent 30rem),
            oklch(var(--b2));
        overflow: hidden;
    }

    .dlna-queue__mobile {
        display: none;
    }

    .dlna-queue__desktop {
        display: flex;
        width: 100%;
        max-width: 1480px;
        height: 100%;
        min-width: 0;
        margin-left: auto;
        margin-right: auto;
    }

    .dlna-queue__lyrics {
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
    }

    .dlna-queue__lyrics-current,
    .dlna-queue__lyrics-panel,
    .dlna-queue__panel,
    .dlna-queue__desktop-now,
    .dlna-queue__desktop-toolbar,
    .dlna-queue__tracklist {
        border-radius: 1.35rem;
        background: oklch(var(--b1) / 0.82);
        box-shadow: 0 16px 34px rgba(148, 163, 184, 0.12);
        backdrop-filter: blur(18px);
    }

    .dlna-queue__lyrics-current {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem;
    }

    .dlna-queue__lyrics-cover-link,
    .dlna-queue__lyrics-cover {
        width: 3.5rem;
        height: 3.5rem;
        flex-shrink: 0;
    }

    .dlna-queue__lyrics-cover {
        object-fit: cover;
        border-radius: 0.95rem;
    }

    .dlna-queue__lyrics-label {
        color: oklch(var(--bc) / 0.52);
        font-size: 0.72rem;
        font-weight: 700;
    }

    .dlna-queue__lyrics-title {
        display: block;
        margin-top: 0.15rem;
        color: oklch(var(--bc));
        font-size: 0.92rem;
        font-weight: 800;
    }

    .dlna-queue__lyrics-artist {
        display: block;
        margin-top: 0.15rem;
        color: oklch(var(--bc) / 0.56);
        font-size: 0.76rem;
    }

    .dlna-queue__icon-button {
        width: 2.25rem;
        height: 2.25rem;
        background: oklch(var(--b2) / 0.9);
        color: oklch(var(--bc));
    }

    .dlna-queue__icon-button svg {
        width: 1.05rem;
        height: 1.05rem;
    }

    .dlna-queue__lyrics-panel {
        min-height: 0;
        flex: 1;
        overflow: hidden;
    }

    .dlna-queue__lyrics-scroll {
        overflow-y: auto;
        padding: 2rem 1rem;
    }

    .dlna-queue__lyrics-list {
        transition: transform 0.28s ease;
    }

    .dlna-queue__lyric-line {
        margin-bottom: 0.7rem;
        border: 0;
        background: transparent;
        color: oklch(var(--bc) / 0.44);
        cursor: pointer;
        list-style: none;
        text-align: center;
        font-size: 0.95rem;
        line-height: 1.55;
        transition: color 0.18s ease, font-size 0.18s ease;
    }

    .dlna-queue__lyric-line.is-active {
        color: oklch(var(--p));
        font-size: 1.22rem;
        font-weight: 800;
    }

    .dlna-queue__lyrics-empty {
        padding-top: 6rem;
        color: oklch(var(--bc) / 0.48);
        text-align: center;
        font-weight: 700;
    }

    .dlna-queue__panel {
        min-width: 0;
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.85rem;
        padding: 1rem;
        overflow: hidden;
    }

    .dlna-queue__desktop-header {
        min-height: 3rem;
    }

    .dlna-queue__collapse {
        width: 2.6rem;
        height: 2.6rem;
        background: oklch(var(--b2) / 0.88);
        color: oklch(var(--bc));
    }

    .dlna-queue__collapse svg {
        width: 1.15rem;
        height: 1.15rem;
    }

    .dlna-queue__desktop-title-block {
        min-width: 0;
        flex: 1;
    }

    .dlna-queue__desktop-title {
        margin-top: 0.12rem;
        color: oklch(var(--bc));
        font-size: 1.55rem;
        line-height: 1.05;
        font-weight: 800;
        letter-spacing: -0.03em;
    }

    .dlna-queue__status {
        border-radius: 999px;
        padding: 0.45rem 0.75rem;
        background: oklch(var(--p));
        color: oklch(var(--pc));
        font-size: 0.76rem;
        font-weight: 800;
        white-space: nowrap;
    }

    .dlna-queue__selector {
        flex-shrink: 0;
    }

    .dlna-queue__output-switch {
        display: inline-flex;
        align-items: center;
        gap: 0.22rem;
        padding: 0.22rem;
        border: 1px solid oklch(var(--bc) / 0.1);
        border-radius: 999px;
        background: oklch(var(--b1) / 0.74);
        box-shadow: 0 10px 24px oklch(var(--bc) / 0.08);
        flex-shrink: 0;
    }

    .dlna-queue__output-button,
    .dlna-queue__groupcast-button {
        min-height: 2.3rem;
        border: 0;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.38rem;
        padding: 0 0.82rem;
        font-size: 0.76rem;
        font-weight: 800;
        white-space: nowrap;
        transition: transform 0.18s ease, background 0.18s ease, color 0.18s ease;
    }

    .dlna-queue__output-button {
        background: transparent;
        color: oklch(var(--bc) / 0.62);
    }

    .dlna-queue__output-button.is-active {
        background: oklch(var(--bc));
        color: oklch(var(--b1));
    }

    .dlna-queue__groupcast-button {
        border: 1px solid oklch(var(--bc) / 0.1);
        background: oklch(var(--b1) / 0.78);
        color: oklch(var(--bc));
        box-shadow: 0 10px 24px oklch(var(--bc) / 0.08);
        flex-shrink: 0;
    }

    .dlna-queue__output-button:active,
    .dlna-queue__groupcast-button:active {
        transform: scale(0.98);
    }

    .dlna-queue__output-button svg,
    .dlna-queue__groupcast-button svg {
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
    }

    .dlna-queue__desktop-now {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
    }

    .dlna-queue__desktop-cover {
        width: 5.5rem;
        height: 5.5rem;
        border-radius: 1.15rem;
        object-fit: cover;
    }

    .dlna-queue__desktop-label {
        color: oklch(var(--bc) / 0.52);
        font-size: 0.76rem;
        font-weight: 800;
    }

    .dlna-queue__desktop-track {
        margin-top: 0.18rem;
        color: oklch(var(--bc));
        font-size: 1.28rem;
        font-weight: 850;
    }

    .dlna-queue__desktop-artist {
        margin-top: 0.2rem;
        color: oklch(var(--bc) / 0.58);
        font-size: 0.88rem;
    }

    .dlna-queue__desktop-controls {
        display: flex;
        align-items: center;
        gap: 0.55rem;
    }

    .dlna-queue__control {
        width: 2.7rem;
        height: 2.7rem;
        background: oklch(var(--b2) / 0.9);
        color: oklch(var(--bc));
    }

    .dlna-queue__control.is-play {
        width: 3.2rem;
        height: 3.2rem;
        background: oklch(var(--bc));
        color: oklch(var(--b1));
    }

    .dlna-queue__control svg {
        width: 1.22rem;
        height: 1.22rem;
    }

    .dlna-queue__desktop-toolbar {
        display: flex;
        flex-wrap: wrap;
        gap: 0.65rem;
        padding: 0.75rem;
    }

    .dlna-queue__desktop-tool {
        min-height: 2.35rem;
        border: 0;
        border-radius: 999px;
        padding: 0 0.95rem;
        background: oklch(var(--b2) / 0.9);
        color: oklch(var(--bc));
        font-size: 0.78rem;
        font-weight: 800;
    }

    .dlna-queue__tracklist {
        min-height: 0;
        flex: 1;
        padding: 0.9rem;
        overflow-y: auto;
    }

    .dlna-queue__empty--desktop {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin: 0;
        background: oklch(var(--b1) / 0.82);
    }
}

@media (prefers-reduced-motion: reduce) {
    .dlna-queue__row,
    .dlna-queue__lyrics-list,
    .dlna-queue__lyric-line {
        transition: none;
    }
}

[data-theme="dark"] .dlna-queue__mobile-title,
[data-theme="dark"] .dlna-queue__row-title,
[data-theme="dark"] .dlna-queue__empty-title,
[data-theme="dark"] .dlna-queue__empty-icon {
    color: oklch(var(--bc));
}

[data-theme="dark"] .dlna-queue__action,
[data-theme="dark"] .dlna-queue__tool,
[data-theme="dark"] .dlna-queue__row,
[data-theme="dark"] .dlna-queue__empty {
    background: oklch(var(--b1) / 0.78);
    color: oklch(var(--bc));
}

[data-theme="dark"] .dlna-queue__row.is-current {
    background: linear-gradient(135deg, oklch(var(--b2) / 0.96), oklch(var(--b3) / 0.86));
}

[data-theme="dark"] .resize-handle:hover,
[data-theme="dark"] .resize-handle.resizing {
    background-color: rgba(255, 255, 255, 0.2);
}
</style>
